import { h } from '@stencil/core';
import type { ObservableMap } from '@stencil/store';

import type { MatchStoreState } from '../../../../../services/interfaces';
import { HomeAwayTeam, PlayerEventSubType } from '../../../../../services/interfaces';
import { getMatchWording, isFinishedMatch, isSuspendedMatch } from '../../helpers';
import {
  getDisplayedPenaltiesCount,
  isLiveMatchPenalties,
  isVerticalLayout,
  matchPenaltyScore,
  matchPenaltyScorers,
  setCssVariant,
} from '../../helpers/match-unit.helpers';

type Props = {
  state: ObservableMap<MatchStoreState>['state'];
};

const MatchPenaltyScore = ({ state }: Props) => {
  const size = state.size;
  const isVertical = isVerticalLayout(size);
  const cssVariant = setCssVariant(size);

  const match = state.matchData;
  if (!match) return null;

  const isLivePenalties = isLiveMatchPenalties(match.status, match.phase);
  const suspendedMatch = isSuspendedMatch(match.status);
  const finishedMatch = isFinishedMatch(match.status);
  const homeTeamPenalties = matchPenaltyScore(match, HomeAwayTeam.HOME);
  const awayTeamPenalties = matchPenaltyScore(match, HomeAwayTeam.AWAY);

  const havePenaltiesScore = isLivePenalties || (finishedMatch && (!!homeTeamPenalties || !!awayTeamPenalties));

  if (!havePenaltiesScore) return null;

  const print = (penaltyScore: number) => {
    const penalty = penaltyScore ?? 0;
    if (isVertical && !isLivePenalties) return `(${penalty})`;

    return penalty;
  };

  return (
    <div
      class={{
        'pk-match__penalties--score': true,
        live: isLivePenalties,
        suspended: suspendedMatch,
        ...cssVariant,
      }}
      part="penalties"
    >
      <span class="home" part="penalties-home">
        {print(homeTeamPenalties)}
      </span>
      {!isVertical && '-'}
      <span class="away" part="penalties-away">
        {print(awayTeamPenalties)}
      </span>
      {!isVertical && getMatchWording('penalty_abbr')}
    </div>
  );
};

const MatchLivePenalties = ({ state }: Props): HTMLDivElement => {
  const cssVariant = setCssVariant(state.size);

  const match = state.matchData;
  if (!match) return null;

  const isLivePenalties: boolean = isLiveMatchPenalties(match.status, match.phase);

  const homeTeamPenalties = matchPenaltyScorers(match, HomeAwayTeam.HOME) ?? [];
  const awayTeamPenalties = matchPenaltyScorers(match, HomeAwayTeam.AWAY) ?? [];
  const penaltySlotCount = getDisplayedPenaltiesCount(homeTeamPenalties, awayTeamPenalties);

  return (
    <div class={{ 'pk-match__penalties': true, hidden: !isLivePenalties, ...cssVariant }} part="penalties-wrapper">
      <div class={{ 'pk-match__penalties--home': true, ...cssVariant }} part="penalties-home-wrapper">
        {!!penaltySlotCount &&
          [...Array(penaltySlotCount)].map((_p: number, i: number) => {
            const penaltyType = homeTeamPenalties?.[i]?.penaltyType;
            const penaltyNumber = i + 1;
            return (
              <span
                key={`home-penalty-${penaltyNumber}`}
                class={{
                  penalty: true,
                  goal: ([PlayerEventSubType.GOAL, PlayerEventSubType.SCORED] as PlayerEventSubType[]).includes(
                    penaltyType,
                  ),
                  missed: ([PlayerEventSubType.MISS, PlayerEventSubType.MISSED] as PlayerEventSubType[]).includes(
                    penaltyType,
                  ),
                }}
                part="penalty-home-number"
              >
                {penaltyNumber}
              </span>
            );
          })}
      </div>
      <MatchPenaltyScore state={state} />
      <div class={{ 'pk-match__penalties--away': true, ...cssVariant }} part="penalties-away-wrapper">
        {!!penaltySlotCount &&
          [...Array(penaltySlotCount)].map((_p: number, i: number) => {
            const penaltyType = awayTeamPenalties?.[i]?.penaltyType;
            const penaltyNumber = i + 1;
            return (
              <span
                key={`away-penalty-${penaltyNumber}`}
                class={{
                  penalty: true,
                  goal: ([PlayerEventSubType.GOAL, PlayerEventSubType.SCORED] as PlayerEventSubType[]).includes(
                    penaltyType,
                  ),
                  missed: ([PlayerEventSubType.MISS, PlayerEventSubType.MISSED] as PlayerEventSubType[]).includes(
                    penaltyType,
                  ),
                }}
                part="penalty-away-number"
              >
                {penaltyNumber}
              </span>
            );
          })}
      </div>
    </div>
  );
};

export { MatchLivePenalties, MatchPenaltyScore };
